// The v2 site header and footer for the React app (/calculator and /compare).
//
// Exactly the markup every static v2 page uses, pinned in scripts/seo/seo.snapshot.json
// ("chrome", taken from the pilot page). SiteChrome.test.jsx renders both and compares
// them with the pin, so a change to the site header fails there and on the static pages.
// Change the static pages and this file together, and only with Pat's approval.
//
// The phone menu (Phase 6D) works as public/design/v2/nav.js does on the static pages:
// the button toggles aria-expanded, and Escape, choosing a link or a click outside the
// header closes it, without trapping focus. The fold itself is site.css, under .vc-js,
// which src/index.js puts on <html> before the app renders.

import React, { useEffect, useRef, useState } from 'react';

const NAV = [
  { href: '/calculator', label: 'Compare finance' },
  { href: '/novated', label: 'Novated leases' },
  { href: '/stamp-duty', label: 'Stamp duty' },
  { href: '/end-of-term', label: 'End of term' },
  { href: '/learn', label: 'Guides' },
  { href: '/about', label: 'About' },
];

// current: 'calculator' | 'compare' puts aria-current="page" on Compare finance
// (Quick Compare has no header link of its own; it stays in the footer).
const CURRENT = { calculator: '/calculator', compare: '/calculator' };

export function SiteHeader({ current }) {
  const here = CURRENT[current];
  const [open, setOpen] = useState(false);
  const header = useRef(null);
  const button = useRef(null);
  const nav = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (nav.current.contains(document.activeElement)) button.current.focus();
      setOpen(false);
    };
    const onClick = (e) => {
      if (!header.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
    };
  }, [open]);

  return (
    <header className="vc-header" ref={header}>
      <div className="vc-container vc-header__inner">
        <a className="vc-header__logo" href="/" aria-label="Veercal home"><img src="/brand/v2/veercal-logo.svg" alt="Veercal" width="127" height="30" /></a>
        <button type="button" className="vc-nav-toggle" aria-expanded={open} aria-controls="siteNav" ref={button} onClick={() => setOpen(!open)}>Menu</button>
        <nav className="vc-nav" id="siteNav" aria-label="Main" ref={nav}>
          {NAV.map((l) => (
            <a key={l.href} href={l.href} aria-current={l.href === here ? 'page' : undefined} onClick={() => setOpen(false)}>{l.label}</a>
          ))}
        </nav>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="vc-footer">
      <div className="vc-container vc-footer__inner">
        <div className="vc-footer__top">
          <div className="vc-footer__brand"><img src="/brand/v2/veercal-logo-reversed.svg" alt="Veercal" width="144" height="34" /><span>Independent maths on Australian car finance. Free, no sign-up, no paywall.</span></div>
          <div className="vc-footer__cols">
            <div className="vc-footer__col"><h2>Tools</h2><a href="/calculator">Calculator</a><a href="/compare">Quick Compare</a><a href="/car-loan-quote-checker">Quote X-ray</a><a href="/novated">Novated</a><a href="/novated-lease-quote-checker">Novated decoder</a><a href="/stamp-duty">Stamp Duty</a><a href="/end-of-term">End of Term</a></div>
            <div className="vc-footer__col"><h2>Learn</h2><a href="/learn">Learn</a><a href="/faq">FAQ</a></div>
            <div className="vc-footer__col"><h2>Veercal</h2><a href="/">Home</a><a href="/about">About</a><a href="/disclaimer">Disclaimer</a><a href="/terms">Terms</a><a href="/privacy">Privacy</a></div>
          </div>
        </div>
        <div className="vc-footer__legal">General information only, not financial advice. Veercal is a calculation and comparison tool and is not a licensed credit or financial adviser. <a href="/disclaimer">Full disclaimer →</a></div>
      </div>
    </footer>
  );
}
